/**
 * @file ProductsElementPanel.tsx
 * @description Панель «Товары» (phase19.16 Р2; uiux-design-r2 §4 ASCII):
 * источник (`ProductsSourceField`), сколько показать, в ряд на ПК
 * [4][3][2], на телефоне [Лентой][Стопкой по 2]; карточка товара — как в
 * каталоге сайта. Р2а (uiux-design-r2a §4): строка «Карточка: «…» — одна
 * на весь сайт [Карточка, шрифт и цвет — в Каталоге →]» (`ProductCardLine`).
 * SB122 (uiux-settings-groups §3): подгруппы «Какие товары» и «Вид».
 * SB120 этап 3б (uiux-sb120-stage3 §2.3, §3): в ряд 1–6 числом, на
 * телефоне схемами (+ «стопкой»); вместо группы «Цвет и шрифт» — строка
 * карточки `ProductCardLine` «…её шрифт и цвет — там же» (TD-436).
 */

import { GRID_PER_ROW, SITE_LIMITS, defaultElementBody } from "@chaiberi/shared";

import { Label } from "../../../ui/label";
import { SelectField } from "../../../ui/SelectField";
import { SettingsSubgroup } from "../../../ui/settings-groups";
import { useSectionForm } from "../section-form-context";
import { ProductsSourceField } from "./ProductsSourceField";
import { DataCountLine } from "../fields/DataCountLine";
import { ProductCardLine } from "./ProductCardLine";
import { GridCountField } from "./GridCountField";
import { PhoneGridField } from "./PhoneGridField";
import type { ElementPanelProps } from "./types";

const DEFAULT_COLS = defaultElementBody("products").perRow;

const LIMITS = [2, 3, 4, 6, 8, 12, 16, 24].filter((n) => n <= SITE_LIMITS.manualProducts);

export function ProductsElementPanel({ element, onChange, path, dataCount, countStale = false }: ElementPanelProps<"products">) {
  const { issueAt } = useSectionForm();
  const set = (patch: Partial<typeof element>) => onChange({ ...element, ...patch });
  return (
    <>
      <SettingsSubgroup title="Какие товары">
        <ProductsSourceField element={element} onChange={onChange} idsError={issueAt(`${path}.ids`)} badgeError={issueAt(`${path}.badge`)} />
        <DataCountLine count={dataCount} limit={element.source === "manual" ? element.ids.length : element.limit} stale={countStale} />
        {element.source !== "manual" ? (
          <div className="flex flex-col gap-1">
            <Label>Сколько показать</Label>
            <SelectField ariaLabel="Сколько товаров показать" value={String(element.limit)} options={[...new Set([...LIMITS, element.limit])].sort((a, b) => a - b).map((n) => ({ value: String(n), label: String(n) }))} onChange={(v) => set({ limit: Number(v) })} />
          </div>
        ) : (
          <p className="type-caption text-on-surface-variant">Покажем все выбранные: {element.ids.length}.</p>
        )}
      </SettingsSubgroup>
      <SettingsSubgroup title="Вид">
        <GridCountField kind="products" field="perRow" values={GRID_PER_ROW} value={element.perRow} fallback={DEFAULT_COLS} path={path} onChange={(perRow) => set({ perRow: perRow ?? DEFAULT_COLS })} />
        <PhoneGridField ariaLabel="Товары на телефоне" value={element.phoneLayout} onChange={(phoneLayout) => set({ phoneLayout })} />
        <ProductCardLine />
      </SettingsSubgroup>
    </>
  );
}
